import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Globe2, Map as MapIcon, MapPinOff, MousePointerClick, X } from "lucide-react";
import { SkeletonChart } from "../../../components/SkeletonLoading/SkeletonLoading";
import { fmtCurrencyBR, fmtNumberBR } from "../../../utils/formatBrl";
import { statisticsService } from "../../../services/api";
import { countryFlag, countryNamePT } from "./countryUtils";
import "./countries.css";

// d3-geo + geometria só são baixados quando a seção chega perto da tela
const WorldMap = lazy(() => import("./WorldMap"));

// Fica no fim da página: só busca/renderiza quando chega perto da viewport,
// e busca uma vez por filtro aplicado (sem custo para quem não rola até aqui).
function useNearViewport(ref) {
  const [near, setNear] = useState(false);
  useEffect(() => {
    if (near || !ref.current) return;
    if (typeof IntersectionObserver === "undefined") { setNear(true); return; }
    const io = new IntersectionObserver(([entry]) => entry.isIntersecting && setNear(true), { rootMargin: "300px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref, near]);
  return near;
}

export default function CountriesSection({ filtersBody }) {
  const rootRef = useRef(null);
  const near = useNearViewport(rootRef);
  const [state, setState] = useState({ body: null, data: null, loading: false });

  useEffect(() => {
    if (!near || !filtersBody || state.body === filtersBody) return;
    setState({ body: filtersBody, data: null, loading: true });
    statisticsService.getCountries(filtersBody)
      .then(res => {
        if (!res.data.success) throw new Error(res.data.error || "Erro ao carregar dados");
        setState(prev => prev.body === filtersBody ? { ...prev, data: res.data.data.countries, loading: false } : prev);
      })
      .catch(err => {
        console.error("Erro ao carregar países:", err);
        setState(prev => prev.body === filtersBody ? { ...prev, loading: false } : prev);
      });
  }, [near, filtersBody, state.body]);

  return (
    <div ref={rootRef}>
      <CountriesContent data={state.data} loading={!near || state.loading || state.body !== filtersBody} />
    </div>
  );
}

function CountriesContent({ data, loading }) {
  const [mode, setMode] = useState("map");
  const [selectedIso, setSelectedIso] = useState(null);

  const items = useMemo(() => data?.items || [], [data]);
  const unknown = data?.unknown;
  const selected = items.find(c => c.iso2 === selectedIso) || null;
  const withSales = items.filter(c => c.approved > 0);
  const maxRevenue = Math.max(0, ...items.map(c => c.revenue));

  if (loading || !data) {
    return (
      <div className="countries-layout">
        <SkeletonChart />
        <SkeletonChart />
      </div>
    );
  }

  return (
    <div className="countries-layout">
      <div className="countries-card countries-card--map">
        <div className="countries-card-header">
          <h3>Vendas por País</h3>
          <div className="countries-mode-toggle" role="group" aria-label="Visualização">
            <button className={mode === "map" ? "active" : ""} onClick={() => setMode("map")}>
              <MapIcon size={14} /> Mapa
            </button>
            <button className={mode === "globe" ? "active" : ""} onClick={() => setMode("globe")}>
              <Globe2 size={14} /> Globo
            </button>
          </div>
        </div>

        <div className="countries-map-wrap">
          <Suspense fallback={<div className="countries-map-loading">Carregando mapa...</div>}>
            <WorldMap items={items} selectedIso={selectedIso} onSelect={setSelectedIso} mode={mode} />
          </Suspense>

          {selected ? (
            <div className="countries-overlay countries-overlay--left countries-detail">
              <button className="countries-detail-close" onClick={() => setSelectedIso(null)} aria-label="Fechar">
                <X size={14} />
              </button>
              <div className="countries-detail-title">
                <span className="countries-flag">{countryFlag(selected.iso2)}</span>
                {countryNamePT(selected.iso2, selected.name)}
              </div>
              <div className="countries-detail-grid">
                <Metric label="Receita" value={fmtCurrencyBR.format(selected.revenue)} hint={`${selected.revenue_share}% do total`} />
                <Metric label="Vendas" value={fmtNumberBR.format(selected.approved)} hint={`${selected.sales_share}% do total`} />
                <Metric label="Ticket médio" value={fmtCurrencyBR.format(selected.avg_ticket)} />
                <Metric label="Conversão" value={`${selected.conversion_rate}%`} hint={`${fmtNumberBR.format(selected.generated)} gerados`} />
                <Metric label="Compradores" value={fmtNumberBR.format(selected.buyers)} />
              </div>
            </div>
          ) : (
            <div className="countries-overlay countries-overlay--left countries-hint">
              <MousePointerClick size={16} />
              <span>{mode === "globe" ? "Arraste para girar · clique num país" : "Clique num país para ver as métricas"}</span>
            </div>
          )}

        </div>
      </div>

      <div className="countries-card countries-card--ranking">
        <div className="countries-card-header">
          <h3>Ranking</h3>
          <span className="countries-card-sub">{withSales.length} {withSales.length === 1 ? "país" : "países"} com vendas</span>
        </div>

        {items.length === 0 ? (
          <p className="countries-empty">Sem vendas com país identificado no período.</p>
        ) : (
          <ol className="countries-ranking">
            {items.map((c, idx) => (
              <li key={c.iso2}>
                <button
                  className={`countries-ranking-item ${c.iso2 === selectedIso ? "active" : ""}`}
                  onClick={() => setSelectedIso(c.iso2 === selectedIso ? null : c.iso2)}
                >
                  <span className="countries-ranking-pos">{idx + 1}</span>
                  <span className="countries-flag">{countryFlag(c.iso2)}</span>
                  <span className="countries-ranking-main">
                    <span className="countries-ranking-row">
                      <span className="countries-ranking-name">{countryNamePT(c.iso2, c.name)}</span>
                      <span className="countries-ranking-revenue">{fmtCurrencyBR.format(c.revenue)}</span>
                    </span>
                    <span className="countries-ranking-bar">
                      <span style={{ width: `${maxRevenue > 0 ? Math.max(2, (c.revenue / maxRevenue) * 100) : 0}%` }} />
                    </span>
                    <span className="countries-ranking-row countries-ranking-meta">
                      <span>{fmtNumberBR.format(c.approved)} vendas · {c.conversion_rate}% conv.</span>
                      <span>{c.revenue_share}%</span>
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ol>
        )}

        {unknown?.approved > 0 && (
          <div className="countries-ranking-unknown">
            <span className="countries-ranking-pos"><MapPinOff size={14} /></span>
            <span className="countries-ranking-main">
              <span className="countries-ranking-row">
                <span className="countries-ranking-name">Sem país identificado</span>
                <span className="countries-ranking-revenue">{fmtCurrencyBR.format(unknown.revenue)}</span>
              </span>
              <span className="countries-ranking-row countries-ranking-meta">
                <span>{fmtNumberBR.format(unknown.approved)} vendas</span>
                <span>{unknown.sales_share}% das vendas</span>
              </span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, hint }) {
  return (
    <div className="countries-metric">
      <div className="countries-metric-label">{label}</div>
      <div className="countries-metric-value">{value}</div>
      {hint && <div className="countries-metric-hint">{hint}</div>}
    </div>
  );
}
